'use client';

import { c as _c } from "react/compiler-runtime";
import { useLocale, useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { useIntentRoutePrefetch } from '@/hooks/use-intent-route-prefetch';
import { defaultTimeZone } from '@/i18n/locales';
import { Link } from '@/i18n/navigation';
import { env } from '@/lib/config/env';
import { useFormattedCurrency } from '@/lib/currency';
import { GiftHandleBadge } from './gift-handle-badge';
import { OutcomeTokenDisplay } from './outcome-token-display';
import { ShareGiftCta } from './share-gift-cta';

/**
 * Visual variants for the canonical row. All variants share the same data
 * shape — the prop just lets callers nudge density and accents without
 * forking the component.
 */
export type GiftRowDisplayVariant = 'inbox' | 'review' | 'success' | 'permalink' | 'contributor';

/** Sender platform — drives which platform glyph the badge shows. */
export type GiftRowSenderPlatform = 'morfi' | 'x';

/** Asset kind — distinguishes USDC deposits from outcome-token shares. */
export type GiftRowAssetKind = 'usdc' | 'outcome_token';

/**
 * Canonical props for `<GiftRow />`. Flat-prop API consumed by every gift
 * surface that needs to render a single contributing deposit.
 */
export interface GiftRowProps {
  /** Sender handle — null when anonymous (FR-052). */
  senderHandle: string | null;
  /** Display name — null when anonymous OR when the sender opted out of handle disclosure. */
  senderDisplayName: string | null;
  /** Sender platform — drives the platform glyph in the badge. Defaults to 'morfi'. */
  senderPlatform?: GiftRowSenderPlatform;
  /** USDC (D6) for usdc rows OR outcome-token base units (D6) for outcome rows. */
  amountBaseUnits: string;
  assetKind: GiftRowAssetKind;
  /** Outcome-token mint pubkey. Required when assetKind === 'outcome_token'. */
  mintPubkey?: string | null;
  /** Backend fallback if the live snapshot hasn't resolved yet. */
  outcomeLabelFallback?: string | null;
  marketTitleFallback?: string | null;
  /** Optional sanitised gift message. */
  message?: string | null;
  /** Stable React key — use deposit pubkey or composite. */
  rowKey: string;
  /** Variant — controls density and accents. Default 'inbox'. */
  variant?: GiftRowDisplayVariant;
  /** Optional right-side action / status slot. Replaces the default amount summary on the right. */
  rightSlot?: ReactNode;
  /** Optional onClick handler for clickable rows. */
  onClick?: () => void;
  /** data-testid override. Default: `gift-row-${rowKey}`. */
  testId?: string;
}

/* -------------------------------------------------------------------------
 * Legacy compatibility shapes — the old <GiftRow variant="received"|"sent"
 * data={GiftRowData}> API used by gifts-sent-list, gifts-received-list, and
 * withdraw-gift-modal. Kept exported so out-of-scope consumers compile, and
 * internally projected onto the canonical row below.
 * -------------------------------------------------------------------------
 */

export type GiftRowVariant = 'received' | 'sent';
export type GiftRowStatus = 'pending' | 'claimed' | 'withdrawn';

/** Inbox-filter discriminator passed to `useGetGifts({ status })`. */
export type GiftStatusFilter = GiftRowStatus | 'all';
export interface GiftRowData {
  opaqueId: string;
  /** On-chain Gift PDA (base58) — new contract id. Legacy callers used
   *  `depositPubkey` so we accept either via an alias on consumers. */
  giftPda?: string;
  /** @deprecated — use `giftPda` (kept for legacy callers). */
  depositPubkey?: string;
  recipientPlatform: 'morfi' | 'x';
  recipientHandle: string;
  assetKind: GiftRowAssetKind;
  /** Amount in base units (USDC: D6, outcome tokens: D6). */
  amount: string;
  message: string;
  anonymous: boolean;
  status: GiftRowStatus;
  createdAt: string;
  canWithdraw?: boolean;
  /** Sender handle — null when anonymous or sender opted out. */
  senderHandle: string | null;
  cycleIndex?: number;
  sequenceNumber?: number;
  /**
   * HandleVault PDA (base58) the deposit targets. Spec 006-handle-gifting —
   * pending received gifts are grouped by vault so the recipient can drain
   * the entire vault with one Claim-all CTA.
   */
  vaultPubkey?: string;
  /** Outcome-token mint pubkey (base58). Required when `assetKind === 'outcome_token'`. */
  mintPubkey?: string;
  /** Pre-fetched outcome label fallback (e.g. "Yes"). */
  outcomeLabel?: string | null;
  /** Pre-fetched market title fallback. */
  marketTitle?: string | null;
}

/** Discriminated union: either the new flat-prop shape OR the legacy data-bag shape. */
export type GiftRowComponentProps = (GiftRowProps & {
  variant?: GiftRowDisplayVariant;
  data?: undefined;
}) | {
  variant: GiftRowVariant;
  data: GiftRowData;
  onWithdraw?: (data: GiftRowData) => void;
};

/* ------------------------------------------------------------------------- */

interface SenderBadgeArgs {
  senderHandle: string | null;
  senderDisplayName: string | null;
  senderPlatform: GiftRowSenderPlatform;
}

/** Resolve the sender label node — handle badge, display name fallback, or anonymous label. */
function renderSenderLabel(args: SenderBadgeArgs): React.ReactNode {
  if (args.senderHandle === null && args.senderDisplayName === null) {
    return <GiftHandleBadge platform="morfi" handle="" anonymous={true} size="md" />;
  }
  if (args.senderHandle !== null && args.senderHandle.length > 0) {
    return <GiftHandleBadge platform={args.senderPlatform} handle={args.senderHandle} size="md" />;
  }
  // Display name only — sender opted out of handle disclosure.
  return <span className="text-body font-medium" data-testid="gift-row-displayname">
      {args.senderDisplayName ?? ''}
    </span>;
}
interface VariantTokens {
  /** Outer container padding. */
  padding: string;
  /** Background tone. */
  background: string;
  /** Border tone. */
  border: string;
  /** Sender label text class. */
  senderText: string;
  /** Amount text class for USDC rows. */
  amountText: string;
}
const VARIANT_TOKENS: Record<GiftRowDisplayVariant, VariantTokens> = {
  inbox: {
    padding: 'p-3',
    background: 'bg-background-primary',
    border: 'border-border-primary',
    senderText: 'text-body',
    amountText: 'text-body font-semibold'
  },
  review: {
    padding: 'p-3',
    background: 'bg-background-secondary',
    border: 'border-border-primary/60',
    senderText: 'text-body',
    amountText: 'text-body font-semibold'
  },
  success: {
    padding: 'p-3',
    background: 'bg-background-secondary',
    border: 'border-border-primary/60',
    senderText: 'text-body',
    amountText: 'text-body font-semibold'
  },
  permalink: {
    padding: 'p-3',
    background: 'bg-background-primary',
    border: 'border-border-primary',
    senderText: 'text-body',
    amountText: 'text-body-lg font-semibold'
  },
  contributor: {
    padding: 'p-3',
    background: 'bg-background-primary',
    border: 'border-border-primary',
    senderText: 'text-body',
    amountText: 'text-body font-semibold'
  }
};

/**
 * Canonical single-gift row used across every Morfi gift surface — inbox,
 * claim review, claim success, permalink, contributor list. Renders sender
 * (or "Anonymous Morfian"), the asset summary on the right (USDC or
 * <OutcomeTokenDisplay /> with live price), and an optional message line.
 *
 * Two prop shapes are accepted:
 *   1. The new canonical flat-prop API (preferred for new call sites).
 *   2. The legacy `{ variant: 'received'|'sent', data: GiftRowData }` shape
 *      consumed by `<GiftsReceivedList />` and `<GiftsSentList />` — kept
 *      working so the inbox tabs don't regress while we migrate.
 */
export function GiftRow(props) {
  const $ = _c(4);
  if (isLegacyProps(props)) {
    let t0;
    if ($[0] !== props) {
      t0 = <LegacyGiftRow {...props} />;
      $[0] = props;
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    return t0;
  }
  let t0;
  if ($[2] !== props) {
    t0 = <CanonicalGiftRow {...props} />;
    $[2] = props;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  return t0;
}
function isLegacyProps(props: GiftRowComponentProps): props is {
  variant: GiftRowVariant;
  data: GiftRowData;
  onWithdraw?: (d: GiftRowData) => void;
} {
  // Legacy props always supply `data`; canonical props never do.
  return 'data' in props && props.data !== undefined;
}

/* -------------------------------------------------------------------------
 * Canonical row — the single source of truth.
 * -------------------------------------------------------------------------
 */

function CanonicalGiftRow(props) {
  const $ = _c(46);
  const t = useTranslations("gift.inbox.row");
  const {
    formatD6
  } = useFormattedCurrency();
  const variant = props.variant ?? "inbox";
  const tokens = VARIANT_TOKENS[variant];
  const senderPlatform = props.senderPlatform ?? "morfi";
  const isOutcomeToken = props.assetKind === "outcome_token";
  const outcomeMint = props.mintPubkey ?? "";
  const testId = props.testId ?? `gift-row-${props.rowKey}`;
  const message = props.message ?? "";
  let t0;
  if ($[0] !== props.senderDisplayName || $[1] !== props.senderHandle || $[2] !== senderPlatform) {
    t0 = renderSenderLabel({
      senderHandle: props.senderHandle,
      senderDisplayName: props.senderDisplayName,
      senderPlatform
    });
    $[0] = props.senderDisplayName;
    $[1] = props.senderHandle;
    $[2] = senderPlatform;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const senderNode = t0;
  let t1;
  bb0: {
    if (props.rightSlot !== undefined) {
      t1 = props.rightSlot;
      break bb0;
    }
    if (isOutcomeToken && outcomeMint.length > 0) {
      const t2 = props.outcomeLabelFallback ?? null;
      const t3 = props.marketTitleFallback ?? null;
      let t4;
      if ($[4] !== outcomeMint || $[5] !== props.amountBaseUnits || $[6] !== t2 || $[7] !== t3) {
        t4 = <OutcomeTokenDisplay mintPubkey={outcomeMint} amountBaseUnits={props.amountBaseUnits} fallbackOutcomeLabel={t2} fallbackMarketTitle={t3} compact={true} />;
        $[4] = outcomeMint;
        $[5] = props.amountBaseUnits;
        $[6] = t2;
        $[7] = t3;
        $[8] = t4;
      } else {
        t4 = $[8];
      }
      t1 = t4;
      break bb0;
    }
    if (isOutcomeToken) {
      const shares = props.amountBaseUnits.length === 0 ? "0" : props.amountBaseUnits;
      const label = props.outcomeLabelFallback ?? null;
      const market = props.marketTitleFallback ?? null;
      let t2;
      if ($[9] !== label || $[10] !== shares || $[11] !== t) {
        t2 = label === null ? t("outcome_shares_only", {
          shares
        }) : t("outcome_shares_with_label", {
          shares,
          outcome: label
        });
        $[9] = label;
        $[10] = shares;
        $[11] = t;
        $[12] = t2;
      } else {
        t2 = $[12];
      }
      let t3;
      if ($[13] !== t2 || $[14] !== tokens.amountText) {
        t3 = <span className={tokens.amountText}>{t2}</span>;
        $[13] = t2;
        $[14] = tokens.amountText;
        $[15] = t3;
      } else {
        t3 = $[15];
      }
      let t4;
      if ($[16] !== market) {
        t4 = market !== null && market.length > 0 ? <span className="truncate text-caption text-foreground-secondary">{market}</span> : null;
        $[16] = market;
        $[17] = t4;
      } else {
        t4 = $[17];
      }
      let t5;
      if ($[18] !== t3 || $[19] !== t4) {
        t5 = <div className="flex flex-col items-end gap-0.5" data-testid="gift-row-outcome-fallback">{t3}{t4}</div>;
        $[18] = t3;
        $[19] = t4;
        $[20] = t5;
      } else {
        t5 = $[20];
      }
      t1 = t5;
      break bb0;
    }
    const t2 = tokens.amountText;
    let t3;
    if ($[21] !== formatD6 || $[22] !== props.amountBaseUnits) {
      t3 = formatD6(props.amountBaseUnits, "compact");
      $[21] = formatD6;
      $[22] = props.amountBaseUnits;
      $[23] = t3;
    } else {
      t3 = $[23];
    }
    let t4;
    if ($[24] !== t3 || $[25] !== tokens.amountText) {
      t4 = <span className={t2}>{t3}</span>;
      $[24] = t3;
      $[25] = tokens.amountText;
      $[26] = t4;
    } else {
      t4 = $[26];
    }
    t1 = t4;
  }
  const rightNode = t1;
  const Container = props.onClick !== undefined ? "button" : "article";
  const t2 = props.onClick !== undefined ? "button" : undefined;
  const t3 = `flex w-full flex-col gap-1 rounded-control border ${tokens.border} ${tokens.background} ${tokens.padding} text-left ${props.onClick !== undefined ? "transition-colors hover:bg-background-tertiary/30 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring" : ""}`;
  let t4;
  if ($[27] !== senderNode || $[28] !== tokens.senderText) {
    t4 = <span className={tokens.senderText}>{senderNode}</span>;
    $[27] = senderNode;
    $[28] = tokens.senderText;
    $[29] = t4;
  } else {
    t4 = $[29];
  }
  let t5;
  if ($[30] !== rightNode) {
    t5 = <span className="flex min-w-0 max-w-[60%] items-center justify-end gap-2">{rightNode}</span>;
    $[30] = rightNode;
    $[31] = t5;
  } else {
    t5 = $[31];
  }
  let t6;
  if ($[32] !== t4 || $[33] !== t5) {
    t6 = <div className="flex items-center justify-between gap-2">{t4}{t5}</div>;
    $[32] = t4;
    $[33] = t5;
    $[34] = t6;
  } else {
    t6 = $[34];
  }
  let t7;
  if ($[35] !== message || $[36] !== t) {
    t7 = message.length > 0 ? <div className="flex flex-col gap-1"><span className="text-caption font-medium text-foreground-secondary">{t("message_label")}</span><p className="whitespace-pre-wrap wrap-break-word text-caption text-foreground-secondary" data-testid="gift-row-message-body">{message}</p></div> : null;
    $[35] = message;
    $[36] = t;
    $[37] = t7;
  } else {
    t7 = $[37];
  }
  let t8;
  if ($[38] !== Container || $[39] !== props.onClick || $[40] !== t2 || $[41] !== t3 || $[42] !== t6 || $[43] !== t7 || $[44] !== testId) {
    t8 = <Container type={t2} onClick={props.onClick} data-testid={testId} className={t3}>{t6}{t7}</Container>;
    $[38] = Container;
    $[39] = props.onClick;
    $[40] = t2;
    $[41] = t3;
    $[42] = t6;
    $[43] = t7;
    $[44] = testId;
    $[45] = t8;
  } else {
    t8 = $[45];
  }
  return t8;
}

/* -------------------------------------------------------------------------
 * Legacy row — the old inbox-only layout (header with timestamp + status
 * pill, footer with View / Withdraw / ShareGiftCta). Wraps the canonical
 * row's data plumbing through the same OutcomeTokenDisplay pipeline.
 * -------------------------------------------------------------------------
 */

function StatusPill(t0) {
  const $ = _c(6);
  const {
    status
  } = t0;
  const t = useTranslations("gift.inbox.row.status");
  let t1;
  bb0: switch (status) {
    case "pending":
      {
        t1 = "bg-feedback-warning-accent/10 text-feedback-warning-content";
        break bb0;
      }
    case "claimed":
      {
        t1 = "bg-feedback-success-accent/10 text-feedback-success-content";
        break bb0;
      }
    default:
      {
        t1 = "bg-background-tertiary text-foreground-secondary";
      }
  }
  const className = t1;
  const t2 = `inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-caption font-medium ${className}`;
  let t3;
  if ($[0] !== status || $[1] !== t) {
    t3 = t(status);
    $[0] = status;
    $[1] = t;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== t2 || $[4] !== t3) {
    t4 = <span className={t2}>{t3}</span>;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
}
interface LegacyGiftRowProps {
  variant: GiftRowVariant;
  data: GiftRowData;
  onWithdraw?: (data: GiftRowData) => void;
}
function LegacyGiftRow(props) {
  const $ = _c(83);
  const t = useTranslations("gift.inbox.row");
  const locale = useLocale();
  const {
    formatD6
  } = useFormattedCurrency();
  const viewHref = `/gifts/${props.data.opaqueId}`;
  const viewIntent = useIntentRoutePrefetch(viewHref);
  let t0;
  bb0: {
    if (props.variant === "sent") {
      let t1;
      if ($[0] !== t) {
        t1 = t("to");
        $[0] = t;
        $[1] = t1;
      } else {
        t1 = $[1];
      }
      let t2;
      if ($[2] !== t1) {
        t2 = <span>{t1}:</span>;
        $[2] = t1;
        $[3] = t2;
      } else {
        t2 = $[3];
      }
      let t3;
      if ($[4] !== props.data.recipientHandle || $[5] !== props.data.recipientPlatform) {
        t3 = <GiftHandleBadge platform={props.data.recipientPlatform} handle={props.data.recipientHandle} size="md" />;
        $[4] = props.data.recipientHandle;
        $[5] = props.data.recipientPlatform;
        $[6] = t3;
      } else {
        t3 = $[6];
      }
      let t4;
      if ($[7] !== t2 || $[8] !== t3) {
        t4 = <span className="inline-flex flex-wrap items-center gap-1">{t2}{t3}</span>;
        $[7] = t2;
        $[8] = t3;
        $[9] = t4;
      } else {
        t4 = $[9];
      }
      t0 = t4;
      break bb0;
    }
    if (props.data.anonymous || props.data.senderHandle === null) {
      let t1;
      if ($[10] !== t) {
        t1 = t("from");
        $[10] = t;
        $[11] = t1;
      } else {
        t1 = $[11];
      }
      let t2;
      if ($[12] !== t1) {
        t2 = <span>{t1}:</span>;
        $[12] = t1;
        $[13] = t2;
      } else {
        t2 = $[13];
      }
      let t3;
      if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
        t3 = <GiftHandleBadge platform="morfi" handle="" anonymous={true} size="md" />;
        $[14] = t3;
      } else {
        t3 = $[14];
      }
      let t4;
      if ($[15] !== t2) {
        t4 = <span className="inline-flex flex-wrap items-center gap-1">{t2}{t3}</span>;
        $[15] = t2;
        $[16] = t4;
      } else {
        t4 = $[16];
      }
      t0 = t4;
      break bb0;
    }
    let t1;
    if ($[17] !== t) {
      t1 = t("from");
      $[17] = t;
      $[18] = t1;
    } else {
      t1 = $[18];
    }
    let t2;
    if ($[19] !== t1) {
      t2 = <span>{t1}:</span>;
      $[19] = t1;
      $[20] = t2;
    } else {
      t2 = $[20];
    }
    let t3;
    if ($[21] !== props.data.senderHandle) {
      t3 = <GiftHandleBadge platform="morfi" handle={props.data.senderHandle} size="md" />;
      $[21] = props.data.senderHandle;
      $[22] = t3;
    } else {
      t3 = $[22];
    }
    let t4;
    if ($[23] !== t2 || $[24] !== t3) {
      t4 = <span className="inline-flex flex-wrap items-center gap-1">{t2}{t3}</span>;
      $[23] = t2;
      $[24] = t3;
      $[25] = t4;
    } else {
      t4 = $[25];
    }
    t0 = t4;
  }
  const counterpartyLabel = t0;
  const isOutcomeToken = props.data.assetKind === "outcome_token";
  let t1;
  if ($[26] !== formatD6 || $[27] !== isOutcomeToken || $[28] !== props.data.amount) {
    t1 = isOutcomeToken ? "" : formatD6(props.data.amount, "compact");
    $[26] = formatD6;
    $[27] = isOutcomeToken;
    $[28] = props.data.amount;
    $[29] = t1;
  } else {
    t1 = $[29];
  }
  const usdcAmountText = t1;
  const outcomeMint = props.data.mintPubkey ?? "";
  const shareGiftAmountText = isOutcomeToken ? `${props.data.amount} (outcome token)` : usdcAmountText;
  const t2 = `gift-row-${props.data.opaqueId}`;
  let t3;
  if ($[30] !== locale || $[31] !== props.data.createdAt) {
    t3 = new Date(props.data.createdAt).toLocaleString(locale, {
      timeZone: defaultTimeZone
    });
    $[30] = locale;
    $[31] = props.data.createdAt;
    $[32] = t3;
  } else {
    t3 = $[32];
  }
  let t4;
  if ($[33] !== t3) {
    t4 = <span className="text-caption text-foreground-secondary">{t3}</span>;
    $[33] = t3;
    $[34] = t4;
  } else {
    t4 = $[34];
  }
  let t5;
  if ($[35] !== counterpartyLabel || $[36] !== t4) {
    t5 = <div className="flex flex-col gap-1 text-body">{counterpartyLabel}{t4}</div>;
    $[35] = counterpartyLabel;
    $[36] = t4;
    $[37] = t5;
  } else {
    t5 = $[37];
  }
  let t6;
  if ($[38] !== isOutcomeToken || $[39] !== usdcAmountText) {
    t6 = !isOutcomeToken ? <span className="text-body-lg font-semibold">{usdcAmountText}</span> : null;
    $[38] = isOutcomeToken;
    $[39] = usdcAmountText;
    $[40] = t6;
  } else {
    t6 = $[40];
  }
  let t7;
  if ($[41] !== props.data.status) {
    t7 = <StatusPill status={props.data.status} />;
    $[41] = props.data.status;
    $[42] = t7;
  } else {
    t7 = $[42];
  }
  let t8;
  if ($[43] !== t6 || $[44] !== t7) {
    t8 = <div className="flex items-center gap-2">{t6}{t7}</div>;
    $[43] = t6;
    $[44] = t7;
    $[45] = t8;
  } else {
    t8 = $[45];
  }
  let t9;
  if ($[46] !== t5 || $[47] !== t8) {
    t9 = <header className="flex flex-wrap items-center justify-between gap-2">{t5}{t8}</header>;
    $[46] = t5;
    $[47] = t8;
    $[48] = t9;
  } else {
    t9 = $[48];
  }
  let t10;
  if ($[49] !== isOutcomeToken || $[50] !== outcomeMint || $[51] !== props.data.amount || $[52] !== props.data.marketTitle || $[53] !== props.data.outcomeLabel) {
    t10 = isOutcomeToken && outcomeMint.length > 0 ? <OutcomeTokenDisplay mintPubkey={outcomeMint} amountBaseUnits={props.data.amount} fallbackOutcomeLabel={props.data.outcomeLabel ?? null} fallbackMarketTitle={props.data.marketTitle ?? null} /> : null;
    $[49] = isOutcomeToken;
    $[50] = outcomeMint;
    $[51] = props.data.amount;
    $[52] = props.data.marketTitle;
    $[53] = props.data.outcomeLabel;
    $[54] = t10;
  } else {
    t10 = $[54];
  }
  let t11;
  if ($[55] !== props.data.message || $[56] !== t) {
    t11 = props.data.message.length > 0 ? <div className="flex flex-col gap-1"><span className="text-caption font-medium text-foreground-secondary">{t("message_label")}</span><p className="whitespace-pre-wrap wrap-break-word text-caption text-foreground-secondary" data-testid="gift-row-message-body">{props.data.message}</p></div> : null;
    $[55] = props.data.message;
    $[56] = t;
    $[57] = t11;
  } else {
    t11 = $[57];
  }
  let t12;
  if ($[58] !== props.data.assetKind || $[59] !== props.data.depositPubkey || $[60] !== props.data.marketTitle || $[61] !== props.data.opaqueId || $[62] !== props.data.outcomeLabel || $[63] !== props.data.recipientHandle || $[64] !== props.data.recipientPlatform || $[65] !== props.variant || $[66] !== shareGiftAmountText || $[67] !== t || $[68] !== viewHref || $[69] !== viewIntent) {
    t12 = props.variant === "sent" ? <ShareGiftCta permalinkUrl={`${env.NEXT_PUBLIC_SITE_URL.replace(/\/$/, "")}/gifts/${props.data.opaqueId}`} recipientDisplay={`@${props.data.recipientHandle}`} recipientPlatform={props.data.recipientPlatform} amountText={shareGiftAmountText} assetKind={props.data.assetKind === "outcome_token" ? "outcome_token" : "usdc"} outcomeLabel={props.data.outcomeLabel ?? null} marketTitle={props.data.marketTitle ?? null} {...props.data.depositPubkey !== undefined ? {
      depositPubkey: props.data.depositPubkey
    } : {}} compact={true} /> : <AnimatedButton asChild={true} variant="secondary" size="sm"><Link href={viewHref} prefetch={false} {...viewIntent}>{t("view_cta")}</Link></AnimatedButton>;
    $[58] = props.data.assetKind;
    $[59] = props.data.depositPubkey;
    $[60] = props.data.marketTitle;
    $[61] = props.data.opaqueId;
    $[62] = props.data.outcomeLabel;
    $[63] = props.data.recipientHandle;
    $[64] = props.data.recipientPlatform;
    $[65] = props.variant;
    $[66] = shareGiftAmountText;
    $[67] = t;
    $[68] = viewHref;
    $[69] = viewIntent;
    $[70] = t12;
  } else {
    t12 = $[70];
  }
  let t13;
  if ($[71] !== props || $[72] !== t) {
    t13 = props.variant === "sent" && props.data.status === "pending" && props.data.canWithdraw ? <AnimatedButton type="button" variant="secondary" size="sm" onClick={() => {
      if (props.onWithdraw !== undefined) {
        props.onWithdraw(props.data);
      }
    }}>{t("withdraw_cta")}</AnimatedButton> : null;
    $[71] = props;
    $[72] = t;
    $[73] = t13;
  } else {
    t13 = $[73];
  }
  let t14;
  if ($[74] !== t12 || $[75] !== t13) {
    t14 = <footer className="flex flex-wrap gap-2">{t12}{t13}</footer>;
    $[74] = t12;
    $[75] = t13;
    $[76] = t14;
  } else {
    t14 = $[76];
  }
  let t15;
  if ($[77] !== t10 || $[78] !== t11 || $[79] !== t14 || $[80] !== t2 || $[81] !== t9) {
    t15 = <article className="flex flex-col gap-2 rounded-control border border-border-primary bg-background-primary p-4" data-testid={t2}>{t9}{t10}{t11}{t14}</article>;
    $[77] = t10;
    $[78] = t11;
    $[79] = t14;
    $[80] = t2;
    $[81] = t9;
    $[82] = t15;
  } else {
    t15 = $[82];
  }
  return t15;
}